import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Button, TimePicker, Row, Col, InputNumber, } from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import moment from "moment";
import useApi from "../../hooks/useApi";
import { getClass, updateClass, ListBranch, ListEmployee, getBelts, } from "../../constants/API/API";
import callApi from "../../constants/API/CallApi";
import { ShowToast, Severty } from "../../helper/toast";

const DAYS_OF_WEEK = [
  { label: "Sunday", value: "SUNDAY" },
  { label: "Monday", value: "MONDAY" },
  { label: "Tuesday", value: "TUESDAY" },
  { label: "Wednesday", value: "WEDNESDAY" },
  { label: "Thursday", value: "THURSDAY" },
  { label: "Friday", value: "FRIDAY" },
  { label: "Saturday", value: "SATURDAY" },
];

const ClassUpdate = ({ editVisible, setEditVisible, selectedClass: classId, refresh, }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [belts, setBelts] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [scheduleFields, setScheduleFields] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);


  useEffect(() => {
    const fetchClassData = async () => {
      if (classId) {
        try {
          const response = await getClass(classId._id);
          if (response.status === 200 && response.data) {
            const data = response.data;

            const transformed = data.schedules.map((schedule) => ({
              scheduleId: schedule._id,
              dayOfWeek: schedule.dayOfWeek,
              timeRanges: schedule.slots.map((slot) => ({
                slotId: slot._id,
                value: [
                  moment(slot?.startTime, "HH:mm"),
                  moment(slot?.endTime, "HH:mm"),
                ],
                trainerIds: [slot?.trainerId],
                capacity: slot?.capacity,
              })),
            }));

            form.setFieldsValue({
              ...data,
              name: data.name,
              branchId: data.branchId?._id,
              beltId: data.beltId,
              schedules: transformed.map((s) => ({ dayOfWeek: s?.dayOfWeek })),
            });
            setScheduleFields(transformed);
            setSelectedBranch(data?.branchId?._id);
            getTrainers(data?.branchId?._id);
          }
        } catch (err) {
          ShowToast(err?.message || "Failed to load class", Severty.ERROR);
        }
      }
    };
    fetchClassData();
  }, [classId, form]);

  useApi({
    callback: ListBranch,
    dependencies: [],
    shouldCallApi: true,
    onSuccess: ({ list }) => {
      const activeBranches = (list || []).filter((branch) => branch.isActive);
      setBranches(activeBranches);
    },
  });

  useApi({
    callback: getBelts,
    payload: selectedBranch ? { branchId: selectedBranch } : null,
    dependencies: [selectedBranch],
    shouldCallApi: Boolean(selectedBranch),
    onSuccess: ({ data }) => {
      setBelts(data || []);
    },
  });

  const getTrainers = (id) => {
    callApi({
      callback: ListEmployee,
      payload: {
        role: "trainer",
        branchId: id,
      },
      dependencies: [refresh],
      shouldCallApi: true,
      onSuccess: ({ data }) => {
        setTrainers(data?.list || []);
      },
    });
  };

  const handleOk = async (values) => {
    setLoading(true);
    try {
      const { schedules, ...rest } = values;

      // Validate that every schedule has day, trainer, and slot filled
      const isAnyEmpty = schedules.some((schedule, index) => {
        const fieldData = scheduleFields[index];
        const timeRanges = fieldData?.timeRanges || [];

        if (!schedule.dayOfWeek) return true;

        const hasValidSlot = timeRanges.every(
          (t) => t?.value && t?.trainerIds?.length && t?.capacity
        );

        return !hasValidSlot;
      });

      if (isAnyEmpty) {
        ShowToast(
          "Please fill Day, Slot, Trainer, and Age Limits for all schedules.",
          "error"
        );
        setLoading(false);
        return;
      }

      // Format schedules for update
      const formattedSchedules = schedules.map((schedule, index) => {
        const fieldData = scheduleFields[index] || {};
        const timeRanges = fieldData?.timeRanges || [];

        const slots = timeRanges
          .filter((t) => t?.value && t?.trainerIds?.length)
          .flatMap((range) =>
            range.trainerIds.map((trainerId) => ({
              _id: range.slotId,
              startTime: range.value?.[0]?.format("HH:mm"),
              endTime: range.value?.[1]?.format("HH:mm"),
              trainerId,
              capacity: range?.capacity || 0,
            }))
          );

        return {
          _id: fieldData.scheduleId,
          dayOfWeek: schedule.dayOfWeek,
          slots,
        };
      });

      const trainerTimeMap = new Set();
      for (const sched of formattedSchedules) {
        for (const slot of sched.slots) {
          const key = `${sched.dayOfWeek}-${slot.startTime}-${slot.endTime}-${slot.trainerId}`;
          if (trainerTimeMap.has(key)) {
            ShowToast(
              `Trainer is already assigned for ${sched.dayOfWeek} at ${slot.startTime}–${slot.endTime}`,
              Severty.ERROR
            );
            setLoading(false);
            return;
          }
          trainerTimeMap.add(key);
        }
      }

      const payload = {
        ...rest,
        schedules: formattedSchedules,
      };

      // API call
      callApi({
        callback: updateClass,
        payload: [payload, classId._id],
        onSuccess: ({ message }) => {
          ShowToast(message, Severty.SUCCESS);
          refresh(Date.now());
          setEditVisible(false);
          form.resetFields();
          setScheduleFields([]);
          setLoading(false);
        },
        onError: (err) => {
          ShowToast(err, Severty.ERROR);
          setLoading(false);
        },
      });
    } catch (error) {
      console.error("Formatting error", error);
      ShowToast(
        "Something went wrong while formatting schedules",
        Severty.ERROR
      );
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setScheduleFields([]);
    setEditVisible(false);
  };

  const getAvailableDays = (currentIndex) => {
    const schedules = form.getFieldValue("schedules") || [];
    const selectedDays = schedules
      .map((s, i) => (i !== currentIndex ? s?.dayOfWeek : null))
      .filter(Boolean);
    return DAYS_OF_WEEK.filter((day) => !selectedDays.includes(day.value));
  };

  return (
    <Modal
      open={editVisible}
      title="Edit Class"
      okText="Update"
      className="tab_modal"
      onCancel={handleCancel}
      okButtonProps={{ htmlType: "submit", form: "update-form", loading }}
      width={1100}
      destroyOnClose
    >
      <Form form={form} id="update-form" layout="vertical" onFinish={handleOk}>
        <Row gutter={12}>
          <Col span={24} md={8}>
            <Form.Item
              name="name"
              label="Class Name"
              rules={[{ required: true, message: "Please enter class name" }]}
            >
              <Input placeholder="Enter class name" maxLength={100} />
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="branchId"
              label="Branch"
              rules={[{ required: true, message: "Please select branch" }]}
            >
              <Select disabled onChange={(val) => {
                setSelectedBranch(val);
                getTrainers(val);
                form.setFieldsValue({ beltId: [] });
              }}
                placeholder="Select branch">
                {branches.map((b) => (
                  <Select.Option key={b._id} value={b._id}>
                    {b.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="beltId"
              label="belt"
              rules={[{ required: true, message: "Please select belt" }]}
            >
              <Select mode="multiple" placeholder="Select belt">
                {belts.map((b) => (
                  <Select.Option key={b?._id} value={b?._id}>
                    {b?.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="ageLimitStart"
              label="Start Age Limit"
              rules={[
                { required: true, message: "Please enter valid starting age" },
              ]}
            >
              <Input
                min={3}
                max={100}
                type="number"
                placeholder="Enter Start Age Limit"
              />
            </Form.Item>
          </Col>

          <Col span={24} md={8}>
            <Form.Item
              name="ageLimitEnd"
              label="End Age Limit"
              rules={[
                { required: true, message: "Please enter valid ending age" },
              ]}
            >
              <Input
                min={3}
                max={100}
                type="number"
                placeholder="Enter End Age Limit"
              />
            </Form.Item>
          </Col>


        </Row>

        <Form.List name="schedules">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name }, index) => (
                <Row key={key} gutter={[6, 6]} className="mb-3">
                  <Col span={24} md={6}>
                    <Form.Item
                      name={[name, "dayOfWeek"]}
                      rules={[{ required: true, message: "Select a day" }]}
                    >
                      <Select
                        options={getAvailableDays(index)}
                        onChange={(value) => {
                          const updated = [...scheduleFields];
                          updated[index] = {
                            ...updated[index],
                            dayOfWeek: value,
                            timeRanges: updated[index]?.timeRanges || [{}],
                          };
                          setScheduleFields(updated);
                        }}
                      />
                    </Form.Item>
                  </Col>

                  <Col span={24} md={17}>
                    <div className="multiple-timeadd">
                      {(scheduleFields[index]?.timeRanges || [{}]).map(
                        (range, timeIdx) => (
                          <div key={timeIdx} className="multiple-timeadd-main">
                            <TimePicker.RangePicker
                              format="HH:mm"
                              value={range.value}
                              onChange={(value) => {
                                const updated = [...scheduleFields];
                                if (!updated[index])
                                  updated[index] = { timeRanges: [] };
                                updated[index].timeRanges[timeIdx] = {
                                  ...updated[index].timeRanges[timeIdx],
                                  value,
                                };
                                setScheduleFields(updated);
                              }}
                            />

                            <Select
                              mode="multiple"
                              placeholder="Select Trainers"
                              value={range.trainerIds}
                              getPopupContainer={(trigger) =>
                                trigger.parentNode
                              }
                              onChange={(trainerIds) => {
                                const updated = [...scheduleFields];
                                if (!updated[index])
                                  updated[index] = { timeRanges: [] };
                                updated[index].timeRanges[timeIdx] = {
                                  ...updated[index]?.timeRanges[timeIdx],
                                  trainerIds,
                                };
                                setScheduleFields(updated);
                              }}
                              style={{ width: "100%", marginTop: 8 }}
                            >
                              {trainers.map((t) => (
                                <Select.Option key={t._id} value={t._id}>
                                  {t.name}
                                </Select.Option>
                              ))}
                            </Select>

                            <div style={{ width: "100%" }}>
                              <InputNumber
                                placeholder="Enter Class Capacity"
                                min={0}
                                value={
                                  scheduleFields?.[index]?.timeRanges?.[timeIdx]
                                    ?.capacity ?? null
                                }
                                onChange={(value) => {
                                  const updated = [...scheduleFields];

                                  if (!updated[index])
                                    updated[index] = { timeRanges: [] };
                                  if (!Array.isArray(updated[index].timeRanges))
                                    updated[index].timeRanges = [];
                                  if (!updated[index].timeRanges[timeIdx])
                                    updated[index].timeRanges[timeIdx] = {};

                                  updated[index].timeRanges[timeIdx].capacity =
                                    value;
                                  setScheduleFields(updated);
                                }}
                              />
                            </div>

                            {scheduleFields[index]?.timeRanges?.length > 1 && (
                              <Button
                                className="add-minusclass-time"
                                onClick={() => {
                                  const updated = [...scheduleFields];
                                  updated[index].timeRanges.splice(timeIdx, 1);
                                  setScheduleFields(updated);
                                }}
                                icon={<MinusCircleOutlined />}
                              />
                            )}
                          </div>
                        )
                      )}
                    </div>
                  </Col>
                  <Col span={24} md={1}>
                    <Button
                      className="add-plusclass-time"
                      onClick={() => {
                        const updated = [...scheduleFields];
                        updated[index].timeRanges = [
                          ...(updated[index]?.timeRanges || []),
                          {},
                        ];
                        setScheduleFields(updated);
                      }}
                    >
                      <PlusOutlined />
                    </Button>
                  </Col>
                  {fields.length > 1 && (
                    <Col span={24}>
                      <Button
                        className="minus-linewrep"
                        onClick={() => {
                          remove(name);
                          const updated = [...scheduleFields];
                          updated.splice(index, 1);
                          setScheduleFields(updated);
                        }}
                        icon={<MinusCircleOutlined />}
                      >
                        Remove Day
                      </Button>
                    </Col>
                  )}
                </Row>
              ))}

              <Form.Item>
                <Button
                  onClick={() => {
                    add();
                    setScheduleFields([...scheduleFields, {}]);
                  }}
                  icon={<PlusOutlined />}
                  disabled={scheduleFields?.length >= 7}
                  block
                >
                  Add Class Schedule
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default ClassUpdate;
